import { getSelectOptions, type NormalizedField } from "./form/runtimeModel";

/**
 * GET /maintenance-types/{type}/assets/{id} liefert `{ asset: { external_id, masterdata, … } }`.
 * Ältere Aufrufer haben das Hüllenobjekt als Asset gelesen – dann bleiben Kennung und Stammdaten leer.
 */
export interface WrappedAssetPayload {
  asset_id?: number;
  external_id?: string | null;
  name?: string | null;
  hero_address_id?: unknown;
  sub_location_id?: unknown;
  masterdata?: unknown;
  asset?: WrappedAssetPayload | null;
}

const NESTED_MASTERDATA_KEYS = [
  "masterdata",
  "answers",
  "published_masterdata",
  "effective_masterdata",
  "snapshot_masterdata",
] as const;

export function unwrapMaintenanceAsset(payload: unknown): WrappedAssetPayload {
  const record = asRecord(payload);
  if (!record) return {};
  const nested = asRecord(record.asset);
  if (
    nested &&
    (nested.asset_id != null || nested.external_id != null || Object.prototype.hasOwnProperty.call(nested, "masterdata"))
  ) {
    return nested as WrappedAssetPayload;
  }
  return record as WrappedAssetPayload;
}

/** Top-Level-Schlüssel gewinnen; verschachtelte Blöcke füllen nur fehlende Feldkeys. */
export function flattenMasterdata(raw: unknown): Record<string, unknown> {
  const data = asRecord(raw);
  if (!data) return {};
  const lookup: Record<string, unknown> = {};

  const ingest = (obj: Record<string, unknown>, overwrite: boolean) => {
    for (const [rawKey, rawValue] of Object.entries(obj)) {
      const key = rawKey.trim();
      if (!key || key.startsWith("__")) continue;
      if (overwrite || !Object.prototype.hasOwnProperty.call(lookup, key)) {
        lookup[key] = rawValue;
      }
    }
  };

  ingest(data, true);
  for (const nestKey of NESTED_MASTERDATA_KEYS) {
    const nest = asRecord(data[nestKey]);
    if (nest) ingest(nest, false);
  }
  return lookup;
}

export function positiveId(value: unknown): number | null {
  const n = typeof value === "number" ? value : Number(String(value ?? "").trim());
  return Number.isFinite(n) && Number.isInteger(n) && n > 0 ? n : null;
}

function coerceStoredValue(field: NormalizedField, value: unknown): unknown {
  if (value == null) return value;
  const type = field.effective_field_type;
  if (type === "bool" || type === "checkbox") {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value !== 0;
    const text = String(value).trim().toLowerCase();
    if (["1", "true", "ja", "yes", "wahr"].includes(text)) return true;
    if (["0", "false", "nein", "no", "falsch", ""].includes(text)) return false;
    return Boolean(value);
  }
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const rec = value as Record<string, unknown>;
    if ("value" in rec && rec.value != null && typeof rec.value !== "object") {
      return alignSelectValue(field, rec.value);
    }
  }
  return alignSelectValue(field, value);
}

function alignSelectValue(field: NormalizedField, value: unknown): unknown {
  if (field.effective_field_type !== "select" && field.effective_field_type !== "multiselect") return value;
  if (field.effective_field_type === "multiselect" && Array.isArray(value)) {
    return value.map((entry) => alignSelectValue({ ...field, effective_field_type: "select" }, entry));
  }
  if (value == null || value === "") return value;
  const options = getSelectOptions(field);
  const text = String(value);
  if (options.some((option) => option.value === text)) return text;
  const byLabel = options.find((option) => option.label === text);
  return byLabel ? byLabel.value : value;
}

export interface AssetEditPrefill {
  values: Record<string, unknown>;
  externalId: string;
  heroAddressId: number | null;
  subLocationId: number | null;
}

export function buildAssetEditPrefill(
  payload: unknown,
  fields: NormalizedField[],
  fallback: { heroAddressId?: number | null; subLocationId?: number | null; externalId?: string } = {},
): AssetEditPrefill {
  const asset = unwrapMaintenanceAsset(payload);
  const flat = flattenMasterdata(asset.masterdata);
  const values: Record<string, unknown> = {};
  for (const field of fields) {
    values[field.field_key] = field.value ?? null;
  }
  Object.assign(values, flat);
  for (const field of fields) {
    if (Object.prototype.hasOwnProperty.call(flat, field.field_key)) {
      values[field.field_key] = coerceStoredValue(field, flat[field.field_key]);
    }
  }

  const externalId = String(asset.external_id || flat.external_id || flat.kennung || fallback.externalId || "").trim();
  if (externalId) values.external_id = externalId;
  if (asset.name && (values.name == null || values.name === "")) values.name = asset.name;

  return {
    values,
    externalId,
    heroAddressId: positiveId(flat.hero_address_id ?? asset.hero_address_id) ?? fallback.heroAddressId ?? null,
    subLocationId: positiveId(flat.sub_location_id ?? asset.sub_location_id) ?? fallback.subLocationId ?? null,
  };
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed.startsWith("{")) return null;
    try {
      return asRecord(JSON.parse(trimmed));
    } catch {
      return null;
    }
  }
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}
